import classnames from "classnames";
import { Button } from "primereact/button";
import { getVariantStyle, themeClasses } from "sj-ui-toolkit/src/atlas/utils";

export interface ContentCardProps {
  title: string;
  description: string | React.ReactNode;
  image?: string;
  imageAlt?: string;
  imageClassName?: string;
  buttonLabel?: string;
  buttonIcon?: string;
  onClick?: () => void;
  disabled?: boolean;
  selected?: boolean;
  variant?: "mauve" | "blue" | "teal" | "indigo";
  className?: string;
}

export function ContentCard({
  title,
  description,
  image,
  imageAlt = "",
  imageClassName,
  buttonLabel,
  buttonIcon,
  onClick,
  disabled = false,
  selected = false,
  variant = "mauve",
  className,
}: ContentCardProps) {
  const variantStyle = getVariantStyle(variant);

  return (
    <div
      className={classnames(
        "relative flex flex-col rounded-md overflow-hidden",
        "transition-all p-8 justify-between h-full",
        themeClasses.bgSurface0,
        {
          "opacity-50 cursor-not-allowed": disabled,
          "hover:shadow-md": !disabled,
        },
        variantStyle,
        className
      )}
    >
      {/* Inline SVG background so that we can apply different strokes based on variant */}
      <svg
        className="bgSvg absolute top-0 right-0 left-auto w-[70%] h-full pointer-events-none"
        viewBox="0 150 320 244"
        fill="none"
      >
        <path
          d="M244.622 -93.3089C261.661 -89.9259 276.572 -82.3947 291.469 -75.1985C319.719 -61.5488 349.721 -57.0201 380.612 -55.6902C390.846 -55.2487 400.833 -52.8101 409.798 -47.2993C421.32 -40.2146 427.146 -29.0413 428.943 -15.7108C433.778 20.0933 445.568 53.9218 453.604 88.7971C460.058 116.828 461.171 144.902 441.112 169.485C425.587 188.509 406.083 200.8 383.504 208.18C355.686 217.272 327.843 219.771 300.231 209.546C283.565 203.374 267.426 195.92 256.675 180.234C249.927 170.391 249.396 159.62 253.479 148.333C256.618 139.658 260.659 131.358 263.011 122.34C270.799 92.484 259.433 66.2544 231.92 54.9171C209.375 45.6271 186.012 42.7798 162.409 53.7523C148.224 60.3423 136.965 70.4742 128.15 83.6969C119.709 96.3669 112.417 109.95 101.086 120.317C91.6448 128.952 75.4435 130.957 64.4182 125.376C45.3552 115.728 30.6757 102.045 24.7247 79.9649C21.3782 67.5412 21.1569 54.7083 23.5594 42.2154C28.2849 17.6118 35.8045 -5.78478 52.3838 -25.514C61.7085 -36.6077 72.9975 -40.8215 85.9876 -42.5853C107.046 -45.4397 127.744 -49.7202 146.022 -62.2508C153.479 -67.3618 160.922 -72.5942 167.849 -78.4422C191.382 -98.3106 217.114 -103.175 244.622 -93.3089Z"
          strokeWidth="1.57272"
          strokeMiterlimit="10"
        />
        <path
          d="M419.712 134.145C413.294 162.565 399.119 175.467 380.148 182.862C361.841 189.996 342.505 192.97 323.629 188.529C292.211 181.138 285.514 164.11 290.303 135.899C293.364 117.868 295.597 99.932 291.296 81.8305C284.797 54.4773 269.347 36.1367 243.035 28.6203C220.916 22.3028 198.336 18.8863 175.197 18.9889C149.233 19.1052 130.473 32.463 115.871 53.8912C109.241 63.6203 103.417 73.9795 95.2887 82.5428C75.2273 103.683 52.3655 87.5758 48.422 67.8108C46.1446 56.4012 48.0241 45.3639 53.9094 35.2162C65.9104 14.5293 83.7794 1.52085 105.29 -5.89704C128.173 -13.7933 146.636 -27.898 163.266 -45.9535C176.794 -60.6405 191.345 -74.7521 212.131 -77.6026C237.635 -81.1001 257.403 -68.0406 276.813 -54.2933C286.334 -47.5487 295.027 -39.4397 306.115 -35.3977C320.617 -30.1086 334.925 -23.7833 349.878 -20.3369C388.703 -11.3881 409.127 15.8912 418.892 53.4146C426.392 82.2399 423.385 111.734 419.708 134.144L419.712 134.145Z"
          strokeWidth="1.57"
          strokeMiterlimit="10"
        />
      </svg>

      {/* Content */}
      <div className="relative flex flex-col gap-4 mb-5 z-10">
        {image && (
          <div className="w-full h-32 overflow-hidden rounded-lg">
            <img
              src={image}
              alt={imageAlt}
              className={classnames("w-full h-full object-contain", imageClassName)}
            />
          </div>
        )}
        {/* Reserve two lines so the rows below line up across cards with 1- and 2-line titles */}
        <h3 className={classnames("atlas-h3-alt min-h-[2lh]", themeClasses.textSurface0)}>
          {title}
        </h3>
        <div className={classnames("body-md", themeClasses.textSurface0)}>{description}</div>
      </div>

      {/* Button Footer */}
      {buttonLabel && onClick && (
        <div className="self-start">
          <Button
            label={buttonLabel}
            icon={buttonIcon}
            onClick={onClick}
            disabled={disabled}
            outlined={!selected}
            severity="contrast"
            className={classnames("z-10", !selected && "border-white text-white")}
          />
        </div>
      )}
    </div>
  );
}
